import { ReactNode } from "react";
import { Box, ButtonBase, Collapse, SvgIconProps, Typography } from "@mui/material";
import emailConfigurationsStyles from "../EmailConfigurations.styles";
import emailConfigurationsTokens from "../EmailConfigurations.tokens";
import { ChevronDownGlyph } from "./EmailConfigIcons";

const styles = emailConfigurationsStyles;
const { section } = emailConfigurationsTokens;

interface EmailConfigSectionProps {
  /** Icon tile glyph — one of the section marks in `EmailConfigIcons`. */
  icon: (props: SvgIconProps) => JSX.Element;
  title: string;
  subtitle: string;
  /** Summary pill; the design only shows it while the section is collapsed. */
  badge?: string;
  open: boolean;
  onToggle: () => void;
  /**
   * Toggle lists (signer permissions) carry their own row padding, so their
   * body drops the flex gap and uses the shorter bottom inset.
   */
  tight?: boolean;
  /** Render nothing: a setting the current host (the embedded wizard) cannot honour. */
  hidden?: boolean;
  children: ReactNode;
}

/**
 * One collapsible settings card on the Email configurations step. The head
 * recolours its icon tile and border while open and swaps the summary pill for
 * the expanded body — exactly as `Email Configurations.dc.html` does.
 */
const EmailConfigSection = ({
  icon: Icon,
  title,
  subtitle,
  badge,
  open,
  onToggle,
  tight = false,
  hidden = false,
  children,
}: EmailConfigSectionProps) => {
  if (hidden) return null;
  const bodyId = `email-config-section-${title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")}`;
  return (
  <Box sx={styles.section(open)}>
    <ButtonBase
      disableRipple
      onClick={onToggle}
      aria-expanded={open}
      aria-controls={bodyId}
      sx={styles.sectionHead}
    >
      <Box sx={styles.sectionIcon(open)}>
        <Icon sx={{ width: section.iconSize, height: section.iconSize }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography component="span" sx={{ ...styles.sectionTitle, display: "block" }}>
          {title}
        </Typography>
        <Typography component="span" sx={{ ...styles.sectionSub, display: "block" }}>
          {subtitle}
        </Typography>
      </Box>
      {!open && !!badge && (
        <Box component="span" sx={styles.sectionBadge}>
          {badge}
        </Box>
      )}
      <ChevronDownGlyph sx={styles.sectionChevron(open)} />
    </ButtonBase>
    <Collapse in={open} unmountOnExit>
      <Box id={bodyId} sx={styles.sectionBody(tight)}>
        {children}
      </Box>
    </Collapse>
  </Box>
  );
};

export default EmailConfigSection;
